import { useEffect, useRef } from "react"

import { useShellStore } from "./shell.store"

/**
 * Rail the side nav while the caller says the width is needed, and give it
 * back afterwards. The caller decides what "needed" means: OIO passes "a draft
 * is generating"; OIA passes "an order is being written OR is on screen",
 * because a nav that springs open the moment the draft lands takes the width
 * back exactly when the officer starts reading.
 *
 * TWO RULES, BOTH ABOUT NOT FIGHTING THE OFFICER.
 *
 * It only ever restores what IT railed. An officer who railed the nav
 * themselves before starting keeps it railed when the draft lands; one who
 * expands it back mid-generation is not overruled, because `navMode` is then
 * no longer `rail` when generation ends and there is nothing to restore.
 *
 * It is not persisted. The nav mode the officer chose is theirs across
 * sessions; this is a borrow for the length of one run.
 *
 * Shared by OIO and OIA deliberately: the two flows had no business growing
 * two copies of a rule about one shell.
 */
export function useRailWhileGenerating(isGenerating: boolean): void {
  const navMode = useShellStore((s) => s.navMode)
  const setNavMode = useShellStore((s) => s.setNavMode)
  const railedByGeneration = useRef(false)

  useEffect(() => {
    if (isGenerating && navMode === "labeled") {
      railedByGeneration.current = true
      setNavMode("rail")
    } else if (!isGenerating && railedByGeneration.current) {
      railedByGeneration.current = false
      if (navMode === "rail") setNavMode("labeled")
    }
    // `navMode` is read, never depended on: reacting to it would re-rail the
    // nav the moment the officer expanded it back mid-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerating])
}
